import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import Chart, {
  ArgumentAxis,
  ValueAxis,
  Series,
  Legend,
  Tooltip,
  Label,
  CommonSeriesSettings,
} from 'devextreme-react/chart';
import { useFafsaData } from '../contexts';
import { FafsaFilterControls } from './FafsaFilterControls';
import { DashboardCard } from '../components/DashboardCard';
import {
    ChartDataPoint,
    GroupedChartDataPoint,
    ChartTooltipArg
} from '../types/FafsaDashboardData';

const FafsaChartContent = () => {
  const { data, loading, error, filter } = useFafsaData();

// Calculate height based on grouping state
  const chartHeight = React.useMemo(() => {
    if (!filter.groupBySchool) {
      return 400;
    }
    // When grouped by school, calculate height based on number of schools
    const schoolCount = new Set(data.map(d => d.schoolName)).size;
    const yearCount = new Set(data.map(d => d.year)).size;
    // More schools = taller chart for readability
    return Math.max(400, schoolCount * yearCount * 40 + 150);
  }, [data, filter.groupBySchool]);

  if (loading) {
    return <Skeleton variant="rounded" height={chartHeight} />;
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Typography align="center" color="textSecondary">
        No FAFSA data available for selected years
      </Typography>
    );
  }

  const prepareChartData = (): ChartDataPoint[] | { data: GroupedChartDataPoint[], schools: string[] } => {
    if (!filter.groupBySchool) {
      return data.map(item => ({
        year: `${item.year}-${item.year + 1}`,
        percentage: item.percentageSubmitted,
        label: `${item.submittedCount}/${item.totalEnrollment}`,
      }));
    } else {
      const chartData: GroupedChartDataPoint[] = [];
      const years = [...new Set(data.map(d => d.year))].sort();
      const schools = [...new Set(data.map(d => d.schoolName).filter((s): s is string => s !== undefined))].sort();

    
    years.forEach(year => {
      const yearData: GroupedChartDataPoint = { year: `${year}-${year + 1}` };
      schools.forEach(school => {
        const item = data.find(d => d.year === year && d.schoolName === school);
        if (item && school) {  // null check for school
          yearData[school] = item.percentageSubmitted;
        }
      });
        chartData.push(yearData);
      });
      
      return { data: chartData, schools };
    }
  };

  const getMaxValue = () => {
    const maxPercentage = Math.max(...data.map(d => d.percentageSubmitted));
    if (maxPercentage < 1) return 5;
    if (maxPercentage < 10) return Math.ceil(maxPercentage + 5);
    return 100;
  };

  if (!filter.groupBySchool) {
    const chartData = prepareChartData() as ChartDataPoint[];
    const maxValue = getMaxValue();
    
    return (
      <Box sx={{ height: '100%' }}>
        <FafsaFilterControls />
        <Chart dataSource={chartData} height={chartHeight}>
            <CommonSeriesSettings
            barPadding={0.3} 
          />
          <ArgumentAxis />
          <ValueAxis 
            visualRange={{ startValue: 0, endValue: maxValue }}
            tickInterval={maxValue <= 10 ? 1 : 10}
            title="Submission Rate (%)"
          />
          <Legend visible={false} />
          <Series
            type="bar"
            valueField="percentage"
            argumentField="year"
            name="FAFSA Submission Rate"
            color="#4CAF50"
            barWidth={100}
          >
            <Label 
              visible={true}
              position="outside"
              customizeText={(arg:  { value: number }) => `${arg.value.toFixed(1)}%`}
            />
          </Series>
          <Tooltip
            enabled={true}
            customizeTooltip={(arg: ChartTooltipArg) => {
              const item = chartData.find(d => d.year === arg.argument);
              if (item) {
                return {
                  text: `${item.percentage.toFixed(1)}% (${item.label})`,
                };
              }
              return { text: `${arg.value.toFixed(1)}%` };
            }}
          />
        </Chart>
         <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          Note: Counts and submission rates are calculated using 12th grade students only
        </Typography>
      </Box>
    );
  } else {
    const result = prepareChartData() as { data: GroupedChartDataPoint[], schools: string[] };
    const { data: chartData, schools } = result;
    const maxValue = getMaxValue();
    
    return (
      <Box sx={{ height: '100%' }}>
        <FafsaFilterControls />
        <Chart dataSource={chartData} height={400}>
          <ArgumentAxis />
          <ValueAxis 
            visualRange={{ startValue: 0, endValue: maxValue }}
            tickInterval={maxValue <= 10 ? 1 : 10}
            title="Submission Rate (%)"
          />
          {schools.map((school: string) => (
            <Series
              key={school}
              type="bar"
              valueField={school}
              argumentField="year"
              name={school}
            >
              <Label 
                visible={true}
                position="outside"
                customizeText={(arg: { value: number }) => `${arg.value.toFixed(1)}%`}
              />
            </Series>
          ))}
          <Legend 
            visible={true}
            horizontalAlignment="center"
            verticalAlignment="bottom"
          />
          <Tooltip
            enabled={true}
            customizeTooltip={(arg: ChartTooltipArg) => {
              const yearString = arg.argument;
              const year = parseInt(yearString.split('-')[0]);
              const schoolName = arg.seriesName;
      
              const item = data.find(d => 
                d.year === year && d.schoolName === schoolName
              );
      
              if (item) {
                return {
                  text: `${schoolName}: ${arg.value?.toFixed(1)}% (${item.submittedCount}/${item.totalEnrollment} students)`,
                };
              }
      
              return {
                text: `${schoolName}: ${arg.value?.toFixed(1)}%`,
              };
            }}
          />
        </Chart>
         <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          Note: Counts and submission rates are calculated using 12th grade students only
        </Typography>
      </Box>
    );
  }
};

export const FafsaChartCard = () => (
  <DashboardCard title="FAFSA Submission Rates">
    <FafsaChartContent />
  </DashboardCard>
);